import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { getTool, getCategoriesAdmin } from '../../graphql/queries';
import { updateTool, deleteTool, getSignedUrl, getObjectUrl } from '../../graphql/mutation';
import {
  Card,
  Form,
  Input,
  Select,
  Upload,
  Button,
  Space,
  Spin,
  Row,
  Col,
  Tag,
  Image,
  Modal,
  InputNumber,
  Divider,
  Progress,
  Alert,
  Radio,
  Switch
} from 'antd';
import { 
  EditOutlined, 
  DeleteOutlined, 
  SaveOutlined, 
  CloseOutlined,
  UploadOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { uploadToR2 } from '../../utils/uploadToR2';
import { showSnackBar } from '../../components/SnackBar/SnackBar';
import { compressVideoToLowRes, compressVideoFromUrl, isFFmpegSupported, getCompressionStatus } from '../../utils/videoCompression';
import './Studios.css';

const { Option } = Select;
const { TextArea } = Input;
const { confirm } = Modal;

const imageTypes = [
  { value: 'DUAL', label: 'Dual' },
  { value: 'SINGLE', label: 'Single' },
];

const toolTypes = [
  { value: 'VIDEO', label: 'Video' },
  { value: 'IMAGE', label: 'Image' },
];

const systemProviders = [
  { value: 'WIRO_PX', label: 'Wiro PX' },
  { value: 'WIRO_KLING', label: 'Wiro Kling' },
  { value: 'WIRO_MINIMAX', label: 'Wiro Minimax' },
  { value: 'WIRO_WAN21', label: 'Wiro Wan21' },
  { value: 'WIRO_BYTEDANCE', label: 'Wiro Bytedance' },
  { value: 'FAL', label: 'Fal' },
  { value: 'WIRO_WAN22_5B', label: 'Wiro Wan22 5B' },
  { value: 'WIRO_WAN22_14B', label: 'Wiro Wan22 14B' },
  { value: 'SERVICE', label: 'Service' },
];

const ToolDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState({
    banner: null,
    video: null,
    lowResVideo: null,
    webm: null,
    webp: null,
  });
  const [previews, setPreviews] = useState({
    banner: null,
    video: null,
    lowResVideo: null,
    webm: null,
    webp: null,
  });
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [isCompressing, setIsCompressing] = useState(false);
  const [isGeneratingLowRes, setIsGeneratingLowRes] = useState(false);
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);

  const { loading: queryLoading, data, refetch } = useQuery(getTool, {
    variables: { getToolId: id },
    onError: (error) => {
      showSnackBar('error', 'Error', 'Failed to load tool details');
    },
  });

  const { data: categoriesData } = useQuery(getCategoriesAdmin);

  const [updateToolMut] = useMutation(updateTool);
  const [deleteToolMut] = useMutation(deleteTool);
  const [getSignedUrlMut] = useMutation(getSignedUrl);
  const [getObjectUrlMut] = useMutation(getObjectUrl);

  const tool = data?.getTool;

  useEffect(() => {
    if (tool && isEditing) {
      form.setFieldsValue({
        name: tool.name,
        skipTranslation: tool.skipTranslation ?? false,
        imageType: tool.imageType,
        templateId: tool.templateId,
        description:
          typeof tool.description === "string"
            ? tool.description
            : tool.description?.en,

        prompt: tool.prompt,
        isFeatured: tool.isFeatured,
        isLivePhoto: tool.isLivePhoto,
        isHidden: tool.isHidden,
        type: tool.type || 'VIDEO',
        duration: tool.duration,
        cost: tool.cost,
        actualCost: tool.actualCost,  
        aspectRatios: tool.aspectRatios || [],
        categoryId: tool.categoryId,
        provider: tool.provider,
        badgeText: tool.badgeText,
        badgeIcon: tool.badgeIcon,
        badgeTextColor: tool.badgeTextColor,
        badgeBackgroundColor: tool.badgeBackgroundColor,
        keywords: tool.keywords || [],
      });
      setPreviews({
        banner: tool.bannerUrl,
        video: tool.videoUrl,
        lowResVideo: tool.lowResolutionUrl,
        webm: tool.webmUrl,
        webp: tool.webpUrl,
      });
    }
  }, [tool, isEditing, form]);

  const handleFileChange = async (type, file) => {
    if (file) {
      const isLt100M = file.size / 1024 / 1024 < 100;
      if (!isLt100M) {
        showSnackBar('error', 'File Too Large', `${type} must be smaller than 100MB`);
        return false;
      }

      // Validate file types
      if (type === 'banner' && !file.type.startsWith('image/')) {
        showSnackBar('error', 'Invalid File Type', 'Banner must be an image file');
        return false;
      }

      if (type === 'video' && !file.type.startsWith('video/')) {
        showSnackBar('error', 'Invalid File Type', 'Video must be a video file');
        return false;
      }

      if (type === 'webm' && file.type !== 'video/webm') {
        showSnackBar('error', 'Invalid File Type', 'WebM file must be a .webm video file');
        return false;
      }

      if (type === 'webp' && file.type !== 'image/webp') {
        showSnackBar('error', 'Invalid File Type', 'WebP file must be a .webp image file');
        return false;
      }

      setFiles(prev => ({ ...prev, [type]: file }));
      setPreviews(prev => ({ 
        ...prev, 
        [type]: URL.createObjectURL(file) 
      }));
      showSnackBar('success', 'File Selected', `${type} file selected successfully`);

      // Auto-generate low resolution video when video is uploaded
      if (type === 'video' && isFFmpegSupported()) {
        try {
          setIsCompressing(true);
          setCompressionProgress(0);
          showSnackBar('info', 'Compressing Video', 'Generating low resolution version...');
          
          const lowResFile = await compressVideoToLowRes(file, setCompressionProgress);
          
          setFiles(prev => ({ ...prev, lowResVideo: lowResFile }));
          setPreviews(prev => ({ 
            ...prev, 
            lowResVideo: URL.createObjectURL(lowResFile) 
          }));
          
          showSnackBar('success', 'Compression Complete', 'Low resolution video generated successfully');
        } catch (error) {
          console.error('Failed to compress video:', error);
          showSnackBar('warning', 'Compression Failed', 'Could not generate low resolution video. You can still proceed.');
        } finally {
          setIsCompressing(false);
          setCompressionProgress(0);
        }
      }
    }
    return false;
  };

  const getSignedUrls = async () => {
    const urls = {};
    for (const [type, file] of Object.entries(files)) {
      if (file) {
        const { data } = await getSignedUrlMut({
          variables: { fileName: file.name }
        });
        urls[type] = data.getSignedUrl;
      }
    }
    return urls;
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFiles({ banner: null, video: null, lowResVideo: null, webm: null, webp: null });
    setPreviews({
      banner: tool?.bannerUrl,
      video: tool?.videoUrl,
      lowResVideo: tool?.lowResolutionUrl,
      webm: tool?.webmUrl,
      webp: tool?.webpUrl,
    });
    form.resetFields();
  };

  const handleUpdate = async (values) => {
    try {
      setLoading(true);

      let bannerUrl = tool.bannerUrl;
      let videoUrl = tool.videoUrl;
      let lowResolutionUrl = tool.lowResolutionUrl;
      let webmUrl = tool.webmUrl;
      let webpUrl = tool.webpUrl;

      // Upload new files if selected
      if (Object.values(files).some(file => file)) {
        const signedUrls = await getSignedUrls();
        
        // Upload files
        const uploadPromises = Object.entries(signedUrls).map(([type, urlData]) => 
          uploadToR2(files[type], urlData.url, urlData.contentType)
        );
        
        if (uploadPromises.length > 0) {
          showSnackBar('info', 'Uploading Files', 'Please wait while files are being uploaded...');
          await Promise.all(uploadPromises);

          // Get permanent URLs
          for (const [type, urlData] of Object.entries(signedUrls)) {
            const { data } = await getObjectUrlMut({
              variables: { key: urlData.id, eTag: 'etag' }
            });
            if (type === 'banner') bannerUrl = data.getObjectUrl;
            if (type === 'video') videoUrl = data.getObjectUrl;
            if (type === 'lowResVideo') lowResolutionUrl = data.getObjectUrl;
            if (type === 'webm') webmUrl = data.getObjectUrl;
            if (type === 'webp') webpUrl = data.getObjectUrl;
          }
        }
      }

      // Filter aspect ratios to only include fields accepted by ToolRatioInput
      const cleanAspectRatios = values.aspectRatios ? values.aspectRatios.map(ratio => ({
        width: ratio.width,
        height: ratio.height,
        name: ratio.name,
        aspectRatioX: ratio.aspectRatioX,
        aspectRatioY: ratio.aspectRatioY,
      })) : [];

      const input = {
        name: values.name,
        skipTranslation: values.skipTranslation ?? false,
        imageType: values.imageType,
        bannerUrl,
        videoUrl,
        lowResolutionUrl,
        webmUrl,
        webpUrl,
        templateId: values.templateId && values.templateId.trim() !== '' ? values.templateId.trim() : null,
        description: values.description || null,
        prompt: values.prompt || null,
        isFeatured: values.isFeatured,
        isLivePhoto: values.isLivePhoto,
        isHidden: values.isHidden,
        type: values.type || 'VIDEO',
        duration: values.duration || null,
        cost: values.cost || null,
        actualCost: values.actualCost || null,
        aspectRatios: cleanAspectRatios,
        categoryId: values.categoryId || null,
        provider: values.provider,
        badgeText: values.badgeText || null,
        badgeIcon: values.badgeIcon || null,
        badgeTextColor: values.badgeTextColor || null,
        badgeBackgroundColor: values.badgeBackgroundColor || null,
        keywords: values.keywords || [],
      };

      await updateToolMut({
        variables: { 
          input,
          updateToolId: id 
        }
      });

      showSnackBar('success', 'Success', 'Tool updated successfully');
      setIsEditing(false);
      setFiles({ banner: null, video: null, lowResVideo: null, webm: null, webp: null });
      refetch();
    } catch (error) {
      showSnackBar('error', 'Error', error.message || 'Failed to update tool');
    } finally {
      setLoading(false);
    }
  };

  const handleVisibilityToggle = async (isHidden) => {
    setIsUpdatingVisibility(true);
    try {
      await updateToolMut({
        variables: {
          input: { isHidden },
          updateToolId: id,
        },
      });
      // Keep the edit form in sync when toggled while editing
      if (isEditing) form.setFieldsValue({ isHidden });
      await refetch();
      showSnackBar('success', 'Updated', `Tool is now ${isHidden ? 'hidden' : 'visible'}`);
    } catch (error) {
      showSnackBar('error', 'Error', error.message || 'Failed to update tool visibility');
    } finally {
      setIsUpdatingVisibility(false);
    }
  };

  const handleDelete = () => {
    confirm({
      title: 'Delete Tool',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to delete this tool? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteToolMut({
            variables: { deleteToolId: id }
          });
          showSnackBar('success', 'Success', 'Tool deleted successfully');
          navigate('/tools');
        } catch (error) {
          showSnackBar('error', 'Error', 'Failed to delete tool');
        }
      },
    });
  };

  // Generate low resolution video from existing video URL
  const handleGenerateLowRes = async () => {
    if (!tool?.videoUrl) {
      showSnackBar('error', 'No Video', 'This tool has no video to compress');
      return;
    }

    if (!isFFmpegSupported()) {
      showSnackBar('error', 'Not Supported', 'Video compression is not supported in this browser');
      return;
    }

    try {
      setIsGeneratingLowRes(true);
      setCompressionProgress(0);
      showSnackBar('info', 'Generating Low Res', 'Downloading and compressing video...');

      // Compress video from URL
      const lowResFile = await compressVideoFromUrl(tool.videoUrl, setCompressionProgress);

      // Get signed URL for upload
      const { data: signedUrlData } = await getSignedUrlMut({
        variables: { fileName: lowResFile.name }
      });

      // Upload to R2
      showSnackBar('info', 'Uploading', 'Uploading compressed video...');
      await uploadToR2(lowResFile, signedUrlData.getSignedUrl.url, signedUrlData.getSignedUrl.contentType);

      // Get permanent URL
      const { data: objectUrlData } = await getObjectUrlMut({
        variables: { key: signedUrlData.getSignedUrl.id, eTag: 'etag' }
      });

      // Update tool with low resolution URL
      await updateToolMut({
        variables: {
          input: { lowResolutionUrl: objectUrlData.getObjectUrl },
          updateToolId: id
        }
      });

      showSnackBar('success', 'Success', 'Low resolution video generated and saved!');
      refetch();
    } catch (error) {
      console.error('Failed to generate low res:', error);
      showSnackBar('error', 'Error', error.message || 'Failed to generate low resolution video');
    } finally {
      setIsGeneratingLowRes(false);
      setCompressionProgress(0);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (queryLoading) {
    return (
      <div className="studios-container">
        <Card>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        </Card>
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="studios-container">
        <Card>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <h3>Tool not found</h3>
            <Button onClick={() => navigate('/tools')}>
              Back to Tools
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="studios-container">
      <Card
        className="studio-detail-card"
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span>Tool Details: {tool.name}</span>
            <Tag color={tool.imageType === 'DUAL' ? 'blue' : 'green'}>
              {tool.imageType}
            </Tag>
            <Tag color={tool.isFeatured ? 'gold' : 'default'}>
              {tool.isFeatured ? 'Featured' : 'Regular'}
            </Tag>
          </div>
        }
        extra={
          <Space>
            <Switch
              checked={tool.isHidden}
              onChange={handleVisibilityToggle}
              loading={isUpdatingVisibility}
              checkedChildren="Hidden"
              unCheckedChildren="Visible"
            />
            <Button onClick={() => navigate('/tools')}>
              Back to List
            </Button>
            {!isEditing ? (
              <>
                <Button 
                  type="primary" 
                  icon={<EditOutlined />} 
                  onClick={handleEdit}
                >
                  Edit
                </Button>
                <Button 
                  danger 
                  icon={<DeleteOutlined />} 
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              </>
            ) : (
              <>
                <Button 
                  type="primary" 
                  icon={<SaveOutlined />} 
                  onClick={() => form.submit()}
                  loading={loading}
                  disabled={isCompressing}
                >
                  Save
                </Button>
                <Button 
                  icon={<CloseOutlined />} 
                  onClick={handleCancel}
                  disabled={isCompressing}
                >
                  Cancel
                </Button>
              </>
            )}
          </Space>
        }
      >
        {!isEditing ? (
          <div className="studio-details-view">
            <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
              {/* Left side - Tool Information */}
              <div style={{ flex: 1 }}>
                <h3 style={{ marginBottom: '20px', color: '#1f1f1f', fontSize: '18px' }}>Tool Information</h3>
                <Row gutter={[20, 12]}>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>ID</label>
                      <div className="detail-value" style={{ fontWeight: '500' }}>{tool.id}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Type</label>
                      <div className="detail-value">
                        <Tag color={tool.imageType === 'DUAL' ? 'blue' : 'green'} style={{ margin: 0 }}>
                          {tool.imageType}
                        </Tag>
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Status</label>
                      <div className="detail-value">
                        <Tag color={tool.isFeatured ? 'gold' : 'default'} style={{ margin: 0 }}>
                          {tool.isFeatured ? 'Featured' : 'Regular'}
                        </Tag>
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Hidden</label>
                      <div className="detail-value">
                        <Tag color={tool.isHidden ? 'red' : 'default'} style={{ margin: 0 }}>
                          {tool.isHidden ? 'Yes (Hidden)' : 'No (Visible)'}
                        </Tag>
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Tool Type</label>
                      <div className="detail-value">
                        <Tag color={tool.type === 'IMAGE' ? 'purple' : 'blue'} style={{ margin: 0 }}>
                          {tool.type || 'VIDEO'}
                        </Tag>
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Live Photo</label>
                      <div className="detail-value">
                        <Tag color={tool.isLivePhoto ? 'green' : 'default'} style={{ margin: 0 }}>
                          {tool.isLivePhoto ? 'Yes' : 'No'}
                        </Tag>
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Template ID</label>
                      <div className="detail-value">{tool.templateId || 'None'}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Duration</label>
                      <div className="detail-value">{tool.duration ? `${tool.duration} seconds` : 'Not set'}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Provider</label>
                      <div className="detail-value">
                        <Tag color="blue" style={{ margin: 0 }}>
                          {tool.provider ? systemProviders.find(p => p.value === tool.provider)?.label || tool.provider : 'Not set'}
                        </Tag>
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Dollar Cost per Generation $</label>
                      <div className="detail-value">{tool.actualCost ? `$${tool.actualCost}` : 'Not set'}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Credit Cost (Auto-calculated)</label>
                      <div className="detail-value">{tool.cost ? `${tool.cost} Credits` : 'Not set'}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Video Count</label>
                      <div className="detail-value" style={{ fontWeight: '500', color: '#1890ff' }}>
                        {tool.videoCount || 0}
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Created</label>
                      <div className="detail-value">{formatDate(tool.createdAt)}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="detail-item">
                      <label>Last Updated</label>
                      <div className="detail-value">{formatDate(tool.updatedAt)}</div>
                    </div>
                  </Col>
                </Row>

                {/* Aspect Ratios Section */}
                {tool.aspectRatios && tool.aspectRatios.length > 0 && (
                  <div style={{ marginTop: '24px' }}>
                    <h3 style={{ marginBottom: '16px', color: '#1f1f1f', fontSize: '18px' }}>Aspect Ratios</h3>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {tool.aspectRatios.map((ratio, index) => (
                        <div 
                          key={index} 
                          style={{ 
                            background: '#f8f9fa', 
                            padding: '12px 16px', 
                            borderRadius: '8px', 
                            border: '1px solid #e9ecef',
                            minWidth: '180px'
                          }}
                        >
                          <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '6px' }}>
                            {ratio.name}
                          </div>
                          <div style={{ fontSize: '13px', color: '#666' }}>
                            <div>Size: {ratio.width} × {ratio.height}</div>
                            <div>Ratio: {ratio.aspectRatioX}:{ratio.aspectRatioY}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {tool.description && (
                  <div style={{ marginTop: '24px' }}>
                    <h3 style={{ marginBottom: '12px', color: '#1f1f1f', fontSize: '18px' }}>Description</h3>
                    <div className="description-text" style={{ 
                      background: '#f8f9fa', 
                      padding: '12px', 
                      borderRadius: '6px', 
                      border: '1px solid #e9ecef',
                      fontSize: '14px',
                      lineHeight: '1.5'
                    }}>
                      {tool.description
                        ? typeof tool.description === "string"
                          ? tool.description
                          : tool.description.en
                        : "—"}
                    </div>
                  </div>
                )}

                {tool.prompt && (
                  <div style={{ marginTop: '24px' }}>
                    <h3 style={{ marginBottom: '12px', color: '#1f1f1f', fontSize: '18px' }}>Prompt</h3>
                    <div className="prompt-text" style={{ 
                      background: '#f8f9fa', 
                      padding: '12px', 
                      borderRadius: '6px', 
                      border: '1px solid #e9ecef',
                      fontSize: '14px',
                      lineHeight: '1.5'
                    }}>
                      {tool.prompt}
                    </div>
                  </div>
                )}

                {tool.keywords && tool.keywords.length > 0 && (
                  <div style={{ marginTop: '24px' }}>
                    <h3 style={{ marginBottom: '12px', color: '#1f1f1f', fontSize: '18px' }}>Keywords</h3>
                    <div>
                      {tool.keywords.map((keyword) => (
                        <Tag key={keyword} style={{ marginBottom: '8px' }}>{keyword}</Tag>
                      ))}
                    </div>
                  </div>
                )}

                {/* Badge Settings */}
                {(tool.badgeText || tool.badgeIcon || tool.badgeTextColor || tool.badgeBackgroundColor) && (
                  <div style={{ marginTop: '24px' }}>
                    <h3 style={{ marginBottom: '16px', color: '#1f1f1f', fontSize: '18px' }}>Badge Settings</h3>
                    <Row gutter={[20, 12]}>
                      {tool.badgeText && (
                        <Col span={12}>
                          <div className="detail-item">
                            <label>Badge Text</label>
                            <div className="detail-value">{tool.badgeText}</div>
                          </div>
                        </Col>
                      )}
                      {tool.badgeIcon && (
                        <Col span={12}>
                          <div className="detail-item">
                            <label>Badge Icon</label>
                            <div className="detail-value">{tool.badgeIcon}</div>
                          </div>
                        </Col>
                      )}
                      {tool.badgeTextColor && (
                        <Col span={12}>
                          <div className="detail-item">
                            <label>Badge Text Color</label>
                            <div className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ 
                                width: '24px', 
                                height: '24px', 
                                background: tool.badgeTextColor,
                                border: '1px solid #d9d9d9',
                                borderRadius: '4px'
                              }} />
                              <span style={{ fontFamily: 'monospace' }}>{tool.badgeTextColor}</span>
                            </div>
                          </div>
                        </Col>
                      )}
                      {tool.badgeBackgroundColor && (
                        <Col span={12}>
                          <div className="detail-item">
                            <label>Badge Background Color</label>
                            <div className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ 
                                width: '24px', 
                                height: '24px', 
                                background: tool.badgeBackgroundColor,
                                border: '1px solid #d9d9d9',
                                borderRadius: '4px'
                              }} />
                              <span style={{ fontFamily: 'monospace' }}>{tool.badgeBackgroundColor}</span>
                            </div>
                          </div>
                        </Col>
                      )}
                    </Row>
                    {/* Badge Preview */}
                    {tool.badgeText && (
                      <div style={{ marginTop: '16px' }}>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '8px', 
                          fontSize: '13px', 
                          fontWeight: '500',
                          color: '#8c8c8c'
                        }}>
                          Badge Preview:
                        </label>
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          background: tool.badgeBackgroundColor || '#1890ff',
                          color: tool.badgeTextColor || '#ffffff',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {tool.badgeIcon && <span>{tool.badgeIcon}</span>}
                          <span>{tool.badgeText}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right side - Media */}
              <div style={{ width: '280px', flexShrink: 0 }}>
                <h3 style={{ marginBottom: '16px', color: '#1f1f1f', fontSize: '18px' }}>Media</h3>
                
                <div style={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      Banner
                    </label>
                    {tool.bannerUrl ? (
                      <Image
                        src={tool.bannerUrl}
                        alt="Banner"
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}
                      />
                    ) : (
                      <div style={{ 
                        width: '100%',
                        height: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f8f9fa', 
                        borderRadius: '6px',
                        border: '1px solid #e9ecef',
                        color: '#8c8c8c',
                        fontSize: '13px'
                      }}>
                        No banner
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      Video
                    </label>
                    {tool.videoUrl ? (
                      <video
                        src={tool.videoUrl}
                        controls
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}
                      />
                    ) : (
                      <div style={{ 
                        width: '100%',
                        height: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f8f9fa', 
                        borderRadius: '6px',
                        border: '1px solid #e9ecef',
                        color: '#8c8c8c',
                        fontSize: '13px',
                        flexDirection: 'column',
                        gap: '4px'
                      }}>
                        <PlayCircleOutlined style={{ fontSize: '16px' }} />
                        No video
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      Low Resolution Video
                    </label>
                    {tool.lowResolutionUrl ? (
                      <video
                        src={tool.lowResolutionUrl}
                        controls
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}
                      />
                    ) : (
                      <div style={{ 
                        width: '100%',
                        minHeight: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f8f9fa', 
                        borderRadius: '6px',
                        border: '1px solid #e9ecef',
                        color: '#8c8c8c',
                        fontSize: '13px',
                        flexDirection: 'column',
                        gap: '8px',
                        padding: '12px'
                      }}>
                        <PlayCircleOutlined style={{ fontSize: '16px' }} />
                        <span>No low res video</span>
                        {tool.videoUrl && isFFmpegSupported() && (
                          <Button
                            type="primary"
                            size="small"
                            onClick={handleGenerateLowRes}
                            loading={isGeneratingLowRes}
                            style={{ marginTop: '4px' }}
                          >
                            Generate Low Res
                          </Button>
                        )}
                        {isGeneratingLowRes && (
                          <Progress 
                            percent={compressionProgress} 
                            size="small" 
                            status="active"
                            style={{ width: '100%', marginTop: '8px' }}
                            format={() => getCompressionStatus(compressionProgress)}
                          />
                        )}
                      </div>
                    )}
                    {tool.lowResolutionUrl && tool.videoUrl && isFFmpegSupported() && (
                      <Button
                        size="small"
                        onClick={handleGenerateLowRes}
                        loading={isGeneratingLowRes}
                        style={{ marginTop: '8px', width: '100%' }}
                      >
                        Regenerate Low Res
                      </Button>
                    )}
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      WebM Video
                    </label>
                    {tool.webmUrl ? (
                      <video
                        src={tool.webmUrl}
                        controls
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}
                      />
                    ) : (
                      <div style={{ 
                        width: '100%',
                        height: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f8f9fa', 
                        borderRadius: '6px',
                        border: '1px solid #e9ecef',
                        color: '#8c8c8c',
                        fontSize: '13px',
                        flexDirection: 'column',
                        gap: '4px'
                      }}>
                        <PlayCircleOutlined style={{ fontSize: '16px' }} />
                        No WebM video
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      WebP Image
                    </label>
                    {tool.webpUrl ? (
                      <Image
                        src={tool.webpUrl}
                        alt="WebP Image"
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}
                      />
                    ) : (
                      <div style={{ 
                        width: '100%',
                        height: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f8f9fa', 
                        borderRadius: '6px',
                        border: '1px solid #e9ecef',
                        color: '#8c8c8c',
                        fontSize: '13px'
                      }}>
                        No WebP image
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleUpdate}
          >
            <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
              {/* Left side - Form Fields */}
              <div style={{ flex: 1 }}>
                <h3 style={{ marginBottom: '20px', color: '#1f1f1f', fontSize: '18px' }}>Edit Tool Information</h3>
                
                <Row gutter={[20, 16]}>
                  <Col span={12}>
                    <Form.Item
                      name="name"
                      label="Tool Name"
                      rules={[{ required: true, message: 'Please enter tool name' }]}
                    >
                      <Input placeholder="Enter tool name" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="skipTranslation"
                      label="Translation"
                    >
                      <Radio.Group>
                        <Radio value={false}>Auto translate</Radio>
                        <Radio value={true}>Do not translate (use original name)</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="imageType"
                      label="Image Type"
                      rules={[{ required: true, message: 'Please select image type' }]}
                    >
                      <Select placeholder="Select image type">
                        {imageTypes.map(type => (
                          <Option key={type.value} value={type.value}>
                            {type.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="templateId"
                      label="Template ID"
                    >
                      <Input
                        style={{ width: '100%' }}
                        placeholder="Enter template ID"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="duration"
                      label="Duration (seconds)"
                    >
                      <InputNumber
                        min={1}
                        max={30}
                        style={{ width: '100%' }}
                        placeholder="Enter duration"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="isFeatured"
                      label="Featured"
                    >
                      <Select placeholder="Is this tool featured?">
                        <Option value={true}>Yes</Option>
                        <Option value={false}>No</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="actualCost"
                      label="Dollar Cost per Generation ($)"
                    >
                      <InputNumber
                      rules={[{ required: true, message: 'Please enter cost' }]}
                        min={0}
                        step={0.01}
                        precision={2}
                        style={{ width: '100%' }}
                        placeholder="Enter cost in dollars"
                        controls={false}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="cost"
                      label="Credit Cost (Auto-calculated)"
                    >
                      <InputNumber
                      disabled
                        min={0}
                        step={0.01}
                        precision={2}
                        style={{ width: '100%' }}
                        placeholder="Enter cost in dollars"
                        controls={false}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="isHidden"
                      label="Hidden"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="Hidden" unCheckedChildren="Visible" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="type"
                      label="Tool Type"
                      rules={[{ required: true, message: 'Please select tool type' }]}
                    >
                      <Select placeholder="Select tool type">
                        {toolTypes.map(type => (
                          <Option key={type.value} value={type.value}>
                            {type.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="isLivePhoto"
                      label="Live Photo"
                    >
                      <Select placeholder="Is this a live photo?">
                        <Option value={true}>Yes</Option>
                        <Option value={false}>No</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="categoryId"
                      label="Category"
                    >
                      <Select placeholder="Select category" allowClear>
                        {categoriesData?.getCategoriesAdmin?.map(category => (
                          <Option key={category.id} value={category.id}>
                            {category.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="provider"
                      label="Provider"
                      rules={[{ required: true, message: 'Please select a provider' }]}
                    >
                      <Select placeholder="Select provider">
                        {systemProviders.map(provider => (
                          <Option key={provider.value} value={provider.value}>
                            {provider.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item label="Aspect Ratios">
                      <Form.List name="aspectRatios">
                        {(fields, { add, remove }) => (
                          <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 16 }}>
                            {fields.map(({ key, name, ...restField }) => (
                              <Row key={key} gutter={8} style={{ marginBottom: 12, alignItems: 'center' }}>
                                <Col span={5}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'name']}
                                    style={{ marginBottom: 0 }}
                                    rules={[{ required: true, message: 'Required' }]}
                                  >
                                    <Input placeholder="Name" size="small" />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'width']}
                                    style={{ marginBottom: 0 }}
                                    rules={[{ required: true, message: 'Required' }]}
                                  >
                                    <InputNumber 
                                      min={1} 
                                      style={{ width: '100%' }}
                                      placeholder="W"
                                      size="small"
                                      controls={false}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={1} style={{ textAlign: 'center', fontSize: 12, color: '#999' }}>
                                  ×
                                </Col>
                                <Col span={4}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'height']}
                                    style={{ marginBottom: 0 }}
                                    rules={[{ required: true, message: 'Required' }]}
                                  >
                                    <InputNumber 
                                      min={1} 
                                      style={{ width: '100%' }}
                                      placeholder="H"
                                      size="small"
                                      controls={false}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'aspectRatioX']}
                                    style={{ marginBottom: 0 }}
                                    rules={[{ required: true, message: 'Required' }]}
                                  >
                                    <InputNumber 
                                      min={1} 
                                      style={{ width: '100%' }}
                                      placeholder="X"
                                      size="small"
                                      controls={false}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={1} style={{ textAlign: 'center', fontSize: 12, color: '#999' }}>
                                  :
                                </Col>
                                <Col span={4}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'aspectRatioY']}
                                    style={{ marginBottom: 0 }}
                                    rules={[{ required: true, message: 'Required' }]}
                                  >
                                    <InputNumber 
                                      min={1} 
                                      style={{ width: '100%' }}
                                      placeholder="Y"
                                      size="small"
                                      controls={false}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={1}>
                                  {fields.length > 1 && (
                                    <Button
                                      type="text"
                                      icon={<MinusCircleOutlined />}
                                      onClick={() => remove(name)}
                                      size="small"
                                      danger
                                      style={{ color: '#ff4d4f' }}
                                    />
                                  )}
                                </Col>
                              </Row>
                            ))}
                            
                            <Button
                              type="dashed"
                              onClick={() => add()}
                              icon={<PlusOutlined />}
                              size="small"
                              style={{ marginTop: 8 }}
                            >
                              Add Ratio
                            </Button>
                          </div>
                        )}
                      </Form.List>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="description"
                  label="Description"
                  style={{ marginTop: '16px' }}
                >
                  <TextArea 
                    rows={3} 
                    placeholder="Enter description"
                  />
                </Form.Item>

                <Form.Item
                  name="prompt"
                  label="Prompt"
                >
                  <TextArea 
                    rows={3} 
                    placeholder="Enter prompt"
                  />
                </Form.Item>

                <Form.Item
                  name="keywords"
                  label="Keywords"
                  tooltip="Words/phrases describing this tool's mood, style, or subject — used for search. Press enter or comma to add."
                >
                  <Select
                    mode="tags"
                    tokenSeparators={[',']}
                    style={{ width: '100%' }}
                    placeholder="e.g. night, cinematic, city"
                    open={false}
                  />
                </Form.Item>

                <Divider orientation="left">Badge Settings</Divider>

                <Form.Item
                  name="badgeText"
                  label="Badge Text"
                >
                  <Input placeholder="Enter badge text (e.g., 'NEW', 'POPULAR')" />
                </Form.Item>

                <Form.Item
                  name="badgeIcon"
                  label="Badge Icon"
                >
                  <Input placeholder="Enter badge icon name (optional)" />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="badgeTextColor"
                      label="Badge Text Color"
                    >
                      <Input 
                        type="color" 
                        style={{ height: 40 }}
                        placeholder="#FFFFFF"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="badgeBackgroundColor"
                      label="Badge Background Color"
                    >
                      <Input 
                        type="color" 
                        style={{ height: 40 }}
                        placeholder="#FF0000"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Right side - Media Upload */}
              <div style={{ width: '280px', flexShrink: 0 }}>
                <h3 style={{ marginBottom: '16px', color: '#1f1f1f', fontSize: '18px' }}>Update Media</h3>
                
                <div style={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      Banner Image
                    </label>
                    <Upload
                      beforeUpload={file => handleFileChange('banner', file)}
                      showUploadList={false}
                      accept="image/*"
                    >
                      <Button 
                        icon={<UploadOutlined />} 
                        style={{ width: '100%', marginBottom: '8px' }}
                        size="small"
                      >
                        {files.banner ? 'Change Banner' : 'Upload Banner'}
                      </Button>
                    </Upload>
                    {previews.banner && (
                      <Image
                        src={previews.banner}
                        alt="Banner preview"
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}
                      />
                    )}
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      Video Preview
                    </label>
                    <Upload
                      beforeUpload={file => handleFileChange('video', file)}
                      showUploadList={false}
                      accept="video/*"
                      disabled={isCompressing}
                    >
                      <Button 
                        icon={isCompressing ? <LoadingOutlined /> : <UploadOutlined />} 
                        style={{ width: '100%', marginBottom: '8px' }}
                        size="small"
                        disabled={isCompressing}
                      >
                        {isCompressing ? 'Compressing...' : (files.video ? 'Change Video' : 'Upload Video')}
                      </Button>
                    </Upload>
                    {isCompressing && (
                      <div style={{ marginBottom: '8px' }}>
                        <Progress 
                          percent={compressionProgress} 
                          size="small" 
                          status="active"
                          format={() => getCompressionStatus(compressionProgress)}
                        />
                      </div>
                    )}
                    {previews.video && (
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>Original:</div>
                        <video
                          src={previews.video}
                          controls
                          style={{ 
                            width: '100%', 
                            maxHeight: '150px',
                            borderRadius: '6px',
                            border: '1px solid #e9ecef'
                          }}
                        />
                      </div>
                    )}
                    {previews.lowResVideo && (
                      <div>
                        <div style={{ fontSize: 11, color: '#52c41a', marginBottom: 4 }}>Low Resolution:</div>
                        <video
                          src={previews.lowResVideo}
                          controls
                          style={{ 
                            width: '100%', 
                            maxHeight: '150px',
                            borderRadius: '6px',
                            border: '1px solid #e9ecef'
                          }}
                        />
                        {files.lowResVideo && (
                          <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                            Size: {(files.lowResVideo.size / 1024 / 1024).toFixed(2)} MB
                          </div>
                        )}
                      </div>
                    )}
                    {!isFFmpegSupported() && (
                      <Alert
                        message="Low res video generation not supported"
                        type="warning"
                        showIcon
                        style={{ marginTop: 8, fontSize: 11 }}
                      />
                    )}
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      WebM Video
                    </label>
                    <Upload
                      beforeUpload={file => handleFileChange('webm', file)}
                      showUploadList={false}
                      accept=".webm,video/webm"
                    >
                      <Button 
                        icon={<UploadOutlined />} 
                        style={{ width: '100%', marginBottom: '8px' }}
                        size="small"
                      >
                        {files.webm ? 'Change WebM' : 'Upload WebM'}
                      </Button>
                    </Upload>
                    {previews.webm && (
                      <video
                        src={previews.webm}
                        controls
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}
                      />
                    )}
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#595959' 
                    }}>
                      WebP Image
                    </label>
                    <Upload
                      beforeUpload={file => handleFileChange('webp', file)}
                      showUploadList={false}
                      accept=".webp,image/webp"
                    >
                      <Button 
                        icon={<UploadOutlined />} 
                        style={{ width: '100%', marginBottom: '8px' }}
                        size="small"
                      >
                        {files.webp ? 'Change WebP' : 'Upload WebP'}
                      </Button>
                    </Upload>
                    {previews.webp && (
                      <Image
                        src={previews.webp}
                        alt="WebP preview"
                        style={{ 
                          width: '100%', 
                          maxHeight: '200px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default ToolDetail; 